/**
 * Shader surgery.
 *
 * Every material in the scene is rewritten on the fly so that *any*
 * `MeshStandardMaterial` an artist or another subsystem creates automatically:
 *
 *   • writes the full MRT G-buffer (view normal + roughness, screen-space
 *     velocity, metalness/AO/matID/emissive),
 *   • is lit by the renderer's analytic sun with cascaded PCSS shadows and
 *     screen-space contact shadows,
 *   • takes its diffuse ambient from the sky SH probe rather than a flat term,
 *   • and receives aerial perspective consistent with the sky.
 *
 * Nothing else in the codebase has to know any of that exists — which is the
 * entire point. Materials that are not lit (basic/points/sprite) still get the
 * G-buffer writes so the extra attachments never contain garbage.
 */

import * as THREE from 'three';
import {
  GLSL_AERIAL, GLSL_COMMON, GLSL_CONTACT, GLSL_CSM, GLSL_SH, GLSL_SUN, GLSL_ATMOSPHERE,
} from './Glsl';
import type { RenderUniforms } from './RenderUniforms';

export type MaterialKind = 'standard' | 'lit' | 'unlit' | 'raw';

const PATCH_VERSION = 10;

/**
 * Diagnostic: replaces the shaded colour with the sun-visibility term
 * (red = cascaded shadow, green = N·L > 0). Flip to `true`, rebuild, capture.
 * Never ship as `true`.
 */
const DEBUG_SHADOW = false;

interface PatchState {
  version: number;
  hook: THREE.Material['onBeforeCompile'];
  shader?: { uniforms: Record<string, THREE.IUniform> };
  matId: THREE.IUniform;
  prevMatrix: THREE.IUniform;
  kind: MaterialKind;
}

interface BOUserData {
  boPatch?: PatchState;
  boSkipMRT?: boolean;
  boMatId?: number;
  boNoSun?: boolean;
}

export function classifyMaterial(m: THREE.Material): MaterialKind | null {
  const mm = m as unknown as Record<string, boolean>;
  if (mm.isRawShaderMaterial) return null;
  if (mm.isMeshStandardMaterial || mm.isMeshPhysicalMaterial) return 'standard';
  if (mm.isMeshLambertMaterial || mm.isMeshPhongMaterial || mm.isMeshToonMaterial) return 'lit';
  if (mm.isShaderMaterial) return 'raw';
  if (
    mm.isMeshBasicMaterial || mm.isPointsMaterial || mm.isSpriteMaterial ||
    mm.isLineBasicMaterial || mm.isMeshMatcapMaterial || mm.isMeshNormalMaterial
  ) return 'unlit';
  return null;
}

/* ------------------------------------------------------------------ GLSL -- */

const MRT_DECL = /* glsl */ `
layout(location = 1) out vec4 boGNormalRough;
layout(location = 2) out vec2 boGVelocity;
layout(location = 3) out vec4 boGMaterial;
uniform vec2 boResolution;
uniform float boMatId;
`;

const VELOCITY_VARYINGS = /* glsl */ `
varying vec4 vBoClipCur;
varying vec4 vBoClipPrev;
`;

const WORLD_VARYINGS = /* glsl */ `
varying vec3 vBoWorldPos;
varying vec3 vBoViewPos;
`;

const VERTEX_PARS = /* glsl */ `
uniform mat4 boViewProjectionNoJitter;
uniform mat4 boPrevViewProjection;
uniform mat4 boPrevModelMatrix;
`;

/** Recomputes the vertex position with the *previous* frame's transform. */
const VERTEX_BODY = /* glsl */ `
{
	vec4 boLocal = vec4( transformed, 1.0 );
	vec4 boPrevLocal = vec4( position, 1.0 );

	#ifdef USE_SKINNING
	{
		mat4 bx = getBoneMatrix( skinIndex.x );
		mat4 by = getBoneMatrix( skinIndex.y );
		mat4 bz = getBoneMatrix( skinIndex.z );
		mat4 bw = getBoneMatrix( skinIndex.w );
		vec4 sv = bindMatrix * vec4( position, 1.0 );
		vec4 sk = vec4( 0.0 );
		sk += bx * sv * skinWeight.x;
		sk += by * sv * skinWeight.y;
		sk += bz * sv * skinWeight.z;
		sk += bw * sv * skinWeight.w;
		boPrevLocal = vec4( ( bindMatrixInverse * sk ).xyz, 1.0 );
	}
	#endif

	#ifdef USE_BATCHING
		boLocal = batchingMatrix * boLocal;
		boPrevLocal = batchingMatrix * boPrevLocal;
	#endif
	#ifdef USE_INSTANCING
		boLocal = instanceMatrix * boLocal;
		boPrevLocal = instanceMatrix * boPrevLocal;
	#endif

	vec4 boWorld = modelMatrix * boLocal;
	vBoWorldPos = boWorld.xyz;
	vBoViewPos = mvPosition.xyz;
	vBoClipCur = boViewProjectionNoJitter * boWorld;
	vBoClipPrev = boPrevViewProjection * ( boPrevModelMatrix * boPrevLocal );
}
`;

const FRAG_PARS_COMMON = GLSL_COMMON;

/** Aerial perspective on its own — a material can want fog without the sun. */
const FRAG_PARS_AERIAL = [GLSL_ATMOSPHERE, GLSL_SUN, GLSL_AERIAL].join('\n');

const FRAG_PARS_LIT = [
  GLSL_ATMOSPHERE,
  GLSL_SUN,
  GLSL_SH,
  GLSL_CSM,
  GLSL_CONTACT,
  /* glsl */ `
uniform vec3 boAmbientSH[ 9 ];
uniform vec3 boAmbientTint;
uniform float boAmbientIntensity;
uniform mat4 projectionMatrix;
float boDbgShadow = 1.0;
float boDbgLit = 0.0;

/**
 * Diffuse albedo of "the ground", used for the one-bounce fill below. Dusty
 * asphalt/stone sits around 0.18-0.22 and is slightly warm; the exact number is
 * not critical, the presence of the term is.
 */
const vec3 BO_GROUND_ALBEDO = vec3( 0.20, 0.175, 0.135 );
`,
].join('\n');

/**
 * Geometric specular anti-aliasing (Kaplanyan et al. / Frostbite's variant).
 *
 * A high-frequency normal map under a tight NDF produces single-pixel highlight
 * sparkle: the sub-pixel normal distribution is far wider than the material's
 * own roughness, so each pixel randomly lands on or off a mirror lobe. TAA
 * cannot settle it because the jitter keeps re-rolling which pixels win, and the
 * result is the crawling glitter you see on cobbles, roof tiles and brick.
 *
 * Widening the NDF by the pixel-footprint normal variance converts that variance
 * into roughness, which is exactly what a correctly filtered material would have
 * had. Injected after the normal is final and before `lights_physical_fragment`
 * builds `material`, so it also reaches the G-buffer roughness that SSR reads.
 */
const SPEC_AA_INJECT = /* glsl */ `
{
	vec3 boDnDx = dFdx( normal );
	vec3 boDnDy = dFdy( normal );
	float boVariance = 0.25 * ( dot( boDnDx, boDnDx ) + dot( boDnDy, boDnDy ) );
	float boKernel = min( 2.0 * boVariance, 0.18 );
	float boAlpha = roughnessFactor * roughnessFactor;
	roughnessFactor = sqrt( clamp( boAlpha + boKernel, 0.0, 1.0 ) );
}
`;

/** Analytic sun evaluated through three's own BRDF so every material feature works. */
const SUN_INJECT = /* glsl */ `
#if defined( RE_Direct )
{
	vec3 boWN = normalize( normal * mat3( viewMatrix ) );
	float boNdotL = dot( boWN, boSunDirWorld );
	if ( boNdotL > 0.0 && ( boSunColor.r + boSunColor.g + boSunColor.b ) > 0.0001 ) {
		float boVD = max( - vBoViewPos.z, 0.0 );
		float boShadow = boSunShadow( vBoWorldPos, boWN, boVD, boNdotL, gl_FragCoord.xy );
		if ( boShadow > 0.02 && boContactParams.x > 0.0 ) {
			boShadow = min( boShadow, boContactShadow(
				vBoViewPos, normalize( normal ), boSunDirView, boNdotL, projectionMatrix, gl_FragCoord.xy ) );
		}
		boDbgShadow = boShadow;
		boDbgLit = 1.0;
		IncidentLight boSun;
		boSun.direction = boSunDirView;
		boSun.color = boSunColor * boShadow;
		boSun.visible = true;
		RE_Direct( boSun, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
	}
}
#endif
`;

/** Replaces three's env-map irradiance with the sky SH probe. */
const MAPS_REPLACE = /* glsl */ `
#if defined( RE_IndirectDiffuse )

	#ifdef USE_LIGHTMAP

		vec4 lightMapTexel = texture2D( lightMap, vLightMapUv );
		irradiance += lightMapTexel.rgb * lightMapIntensity;

	#endif

	#if defined( STANDARD ) || defined( LAMBERT ) || defined( PHONG )

		vec3 boIndN = normalize( normal * mat3( viewMatrix ) );

		// Sky fill, straight off the SH probe.
		vec3 boIndirect = boShIrradiance( boIndN, boAmbientSH );

		// Ground bounce.
		//
		// The SH probe is projected from the sky dome bake alone, so its lower
		// hemisphere is empty. A wall on the shadow side of a building was
		// therefore lit by the half of the sky it can see and by absolutely
		// nothing else, which is why those walls fell away to a couple of per
		// cent grey a few metres up; the underside of an awning, a balcony or a
		// vehicle received nothing at all and read as a hole cut in the scene.
		//
		// Real streets do not work like that: in daylight the ground is the
		// second brightest thing in the frame and throws a warm fill straight
		// back up every vertical surface. One Lambert bounce is enough — the
		// irradiance landing on the ground (sun, cosine-weighted by its
		// elevation, plus the sky's own downward irradiance), reflected by a
		// dusty street albedo, weighted by how much of the receiver's hemisphere
		// lies below the horizon. Ground-facing-up surfaces get none of it, so
		// this cannot flatten the cast shadows on the plaza.
		vec3 boGroundIrr = boShIrradiance( vec3( 0.0, 1.0, 0.0 ), boAmbientSH )
		                 + boSunColor * max( boSunDirWorld.y, 0.0 );
		boIndirect += boGroundIrr * BO_GROUND_ALBEDO * max( 0.5 - 0.5 * boIndN.y, 0.0 );

		iblIrradiance += boIndirect * boAmbientTint * boAmbientIntensity;

	#endif

#endif

#if defined( USE_ENVMAP ) && defined( RE_IndirectSpecular )

	#ifdef USE_ANISOTROPY

		radiance += getIBLAnisotropyRadiance( geometryViewDir, geometryNormal, material.roughness, material.anisotropyB, material.anisotropy );

	#else

		radiance += getIBLRadiance( geometryViewDir, geometryNormal, material.roughness );

	#endif

	#ifdef USE_CLEARCOAT

		clearcoatRadiance += getIBLRadiance( geometryViewDir, geometryClearcoatNormal, material.clearcoatRoughness );

	#endif

#endif
`;

/**
 * G-buffer normal encoding.
 *
 * `gbuffer:normalRoughness` is an RGBA16F attachment and every consumer —
 * `GtaoPass`, `SsrPass` and the passes module's own reference writer in
 * `LabMaterial` — reads `nr.xyz` as a *raw signed* view-space normal and uses
 * `length(n) < 0.1` as the "nothing was written here" sentinel. Writing
 * `n * 0.5 + 0.5` instead pushed every normal into the positive octant (a wall
 * facing (-1,0,0) arrived as (0,0.5,0.5)), which made GTAO's horizon integral
 * over-occlude by up to 5x on flat, unoccluded surfaces and made SSR's backface
 * rejection meaningless. It also made the sentinel unreachable — the shortest
 * encoded vector is 0.37 long. Raw it is.
 */
function mrtWrite(kind: MaterialKind, hasVelocity: boolean, aerial: boolean, sun = false): string {
  const vel = hasVelocity
    ? `vec2 boCur = vBoClipCur.xy / max( abs( vBoClipCur.w ), 1e-5 ) * sign( vBoClipCur.w );
       vec2 boPrv = vBoClipPrev.xy / max( abs( vBoClipPrev.w ), 1e-5 ) * sign( vBoClipPrev.w );
       boGVelocity = ( boCur - boPrv ) * 0.5 * boResolution;`
    : `boGVelocity = vec2( 0.0 );`;

  if (kind === 'standard') {
    return /* glsl */ `
{
	${aerial ? `gl_FragColor.rgb = boAerialPerspective( gl_FragColor.rgb, vBoWorldPos, cameraPosition, boSunDirWorld );` : ''}
	${sun && DEBUG_SHADOW ? `gl_FragColor.rgb = vec3( boDbgShadow, boDbgLit, 0.0 ) * 0.35;` : ''}
	float boAO = 1.0;
	#ifdef USE_AOMAP
		boAO = ambientOcclusion;
	#endif
	float boEm = boSat( boLuma( totalEmissiveRadiance ) * 4.0 );
	boGNormalRough = vec4( normalize( normal ), clamp( roughnessFactor, 0.0, 1.0 ) );
	boGMaterial = vec4( clamp( metalnessFactor, 0.0, 1.0 ), boAO, boMatId, boEm );
	${vel}
}
`;
  }
  if (kind === 'lit') {
    return /* glsl */ `
{
	boGNormalRough = vec4( normalize( normal ), 0.8 );
	boGMaterial = vec4( 0.0, 1.0, boMatId, 0.0 );
	${vel}
}
`;
  }
  // Unlit / unknown: the zero-length sentinel, so AO and SSR skip these pixels
  // instead of occluding a sprite against itself.
  return /* glsl */ `
{
	boGNormalRough = vec4( 0.0, 0.0, 0.0, 1.0 );
	boGMaterial = vec4( 0.0, 1.0, boMatId, 1.0 );
	${vel}
}
`;
}

/* --------------------------------------------------------------- patcher -- */

export interface PatchOptions {
  /** Material id written to the G-buffer (0.5 world, 0.85 view model, 1 sky). */
  matId?: number;
  /** Apply the analytic sun + shadows (false for the view-model scene, which
   *  gets its own rig, and for anything explicitly unlit). */
  sun?: boolean;
  /** Apply distance fog / aerial perspective. */
  aerial?: boolean;
}

export class MaterialPatcher {
  /** Materials we have already rewritten this session. */
  private seen = new WeakSet<THREE.Material>();
  private sharedUniforms: Record<string, THREE.IUniform>;
  private warned = new Set<string>();

  constructor(private uniforms: RenderUniforms) {
    this.sharedUniforms = uniforms.shared();
  }

  /** True when the material now carries our injection. */
  patch(material: THREE.Material, opts: PatchOptions = {}): PatchState | null {
    const ud = material.userData as BOUserData;
    if (ud.boSkipMRT) return null;

    const existing = ud.boPatch;
    if (existing && existing.version === PATCH_VERSION && material.onBeforeCompile === existing.hook) {
      return existing;
    }

    const kind = classifyMaterial(material);
    if (kind === null) {
      const key = material.type;
      if (!this.warned.has(key)) {
        this.warned.add(key);
        console.warn(`[render] material "${key}" left unpatched — it will not write the G-buffer`);
      }
      return null;
    }

    const wantSun = kind === 'standard' && opts.sun !== false && !ud.boNoSun;
    const wantAerial = kind === 'standard' && opts.aerial !== false;

    const matId: THREE.IUniform = { value: ud.boMatId ?? opts.matId ?? 0.5 };
    const prevMatrix: THREE.IUniform = { value: new THREE.Matrix4() };

    const previousHook = Object.prototype.hasOwnProperty.call(material, 'onBeforeCompile')
      ? material.onBeforeCompile
      : null;

    const state: PatchState = {
      version: PATCH_VERSION,
      hook: () => { /* replaced below */ },
      matId,
      prevMatrix,
      kind,
    };

    const shared = this.sharedUniforms;

    const hook: THREE.Material['onBeforeCompile'] = (shader, renderer) => {
      previousHook?.call(material, shader, renderer);

      const hasVertexAnchor = shader.vertexShader.includes('#include <project_vertex>');

      // --- uniforms -----------------------------------------------------
      for (const k in shared) shader.uniforms[k] = shared[k];
      shader.uniforms.boMatId = matId;
      shader.uniforms.boPrevModelMatrix = prevMatrix;

      // --- vertex -------------------------------------------------------
      if (hasVertexAnchor) {
        shader.vertexShader =
          `${WORLD_VARYINGS}\n${VELOCITY_VARYINGS}\n${VERTEX_PARS}\n${shader.vertexShader}`
            .replace(
              '#include <project_vertex>',
              `#include <project_vertex>\n${VERTEX_BODY}`,
            );
      }

      // --- fragment -----------------------------------------------------
      const pars = [
        FRAG_PARS_COMMON,
        wantSun ? FRAG_PARS_LIT : '',
        wantAerial && hasVertexAnchor ? FRAG_PARS_AERIAL : '',
      ].join('\n');
      const varyings = hasVertexAnchor
        ? `${WORLD_VARYINGS}\n${VELOCITY_VARYINGS}`
        : '';

      let frag = `${MRT_DECL}\n${varyings}\n${pars}\n${shader.fragmentShader}`;

      if (kind === 'standard' && frag.includes('#include <normal_fragment_maps>')) {
        frag = frag.replace(
          '#include <normal_fragment_maps>',
          `#include <normal_fragment_maps>\n${SPEC_AA_INJECT}`,
        );
      }

      if (wantSun) {
        frag = frag.replace(
          '#include <lights_fragment_begin>',
          `#include <lights_fragment_begin>\n${SUN_INJECT}`,
        );
        frag = frag.replace('#include <lights_fragment_maps>', MAPS_REPLACE);
      }

      const write = mrtWrite(kind, hasVertexAnchor, wantAerial && hasVertexAnchor, wantSun);
      if (frag.includes('#include <fog_fragment>')) {
        frag = frag.replace('#include <fog_fragment>', `#include <fog_fragment>\n${write}`);
      } else {
        // Last resort: append just before the closing brace of main().
        const i = frag.lastIndexOf('}');
        frag = i >= 0 ? `${frag.slice(0, i)}\n${write}\n}` : frag;
      }

      shader.fragmentShader = frag;
      state.shader = shader as unknown as { uniforms: Record<string, THREE.IUniform> };
    };

    state.hook = hook;
    material.onBeforeCompile = hook;
    // Keeps patched/unpatched variants of the same material class in separate
    // program slots; constant string means settings changes never recompile.
    material.customProgramCacheKey = () => `bo${PATCH_VERSION}:${kind}:${wantSun ? 1 : 0}:${wantAerial ? 1 : 0}`;
    material.needsUpdate = true;
    ud.boPatch = state;
    this.seen.add(material);
    return state;
  }

  /** Per-object previous-frame model matrix, consumed by the velocity buffer. */
  setPrevMatrix(material: THREE.Material, m: THREE.Matrix4): void {
    const st = (material.userData as BOUserData).boPatch;
    if (st) (st.prevMatrix.value as THREE.Matrix4).copy(m);
  }

  setMatId(material: THREE.Material, id: number): void {
    const st = (material.userData as BOUserData).boPatch;
    if (st) st.matId.value = id;
    (material.userData as BOUserData).boMatId = id;
  }
}

/** Marks a material so the patcher leaves it completely alone. */
export function skipPatch<T extends THREE.Material>(m: T): T {
  (m.userData as BOUserData).boSkipMRT = true;
  return m;
}
